/**
 * Notification Service - Client-Side (Firebase Client SDK)
 * 
 * Used in React components for client-side notification operations
 */

import { db } from './config';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  updateDoc, 
  deleteDoc, 
  doc,
  getCountFromServer,
  Timestamp 
} from 'firebase/firestore';

/**
 * Mark notification as read (client-side)
 * @param {string} userId - User ID
 * @param {string} notificationId - Notification document ID
 */
export async function markNotificationAsRead(userId, notificationId) {
  if (!userId || !notificationId) {
    throw new Error('User ID and notification ID are required');
  }

  const notificationRef = doc(db, 'users', userId, 'notifications', notificationId);
  await updateDoc(notificationRef, { read: true });
}

/**
 * Delete a notification (client-side)
 * @param {string} userId - User ID
 * @param {string} notificationId - Notification document ID
 */
export async function deleteNotification(userId, notificationId) {
  if (!userId || !notificationId) {
    throw new Error('User ID and notification ID are required');
  }

  const notificationRef = doc(db, 'users', userId, 'notifications', notificationId);
  await deleteDoc(notificationRef);
}

/**
 * Get unread notifications count (client-side)
 * @param {string} userId - User ID
 * @returns {Promise<number>} - Unread notifications count
 */
export async function getUnreadNotificationsCount(userId) {
  if (!userId) {
    return 0;
  }

  try {
    const notificationsRef = collection(db, 'users', userId, 'notifications');
    const q = query(
      notificationsRef,
      where('read', '==', false)
    );
    const snapshot = await getCountFromServer(q);
    return snapshot.data().count;
  } catch (error) {
    console.error('Error getting unread notifications count:', error);
    return 0;
  }
}

/**
 * Get notifications query for real-time listener (client-side)
 * @param {string} userId - User ID
 * @returns {Query} - Firestore query for unread notifications
 */
export function getNotificationsQuery(userId) {
  if (!userId) {
    return null;
  }

  const notificationsRef = collection(db, 'users', userId, 'notifications');
  return query(
    notificationsRef,
    where('read', '==', false),
    orderBy('createdAt', 'desc')
  );
}
